import type { Locale } from '@/store/preferencesStore';

/**
 * The five supported UI locales, each labelled in its own language. Single
 * source for switchers.
 *
 * No flags: they were emoji, and neither the web font stack nor the mobile
 * app's Inter carries emoji glyphs, so they rendered as tofu boxes. A flag is
 * also the wrong sign for a language — 🇬🇧 excludes most English speakers — and
 * this list disagreed with the iOS one on Portuguese. The switchers show
 * `value.toUpperCase()` as a code badge instead.
 */
export const LOCALES: { value: Locale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
];

export const SUPPORTED_LOCALES: Locale[] = ['en', 'de', 'fr', 'it', 'pt'];

/**
 * Best-effort UI locale from the browser, used only as the initial default for a
 * brand-new visitor with no saved preference. A persisted choice or the account's
 * language always takes precedence over this.
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  const candidates = [navigator.language, ...(navigator.languages ?? [])];
  for (const c of candidates) {
    const base = c?.slice(0, 2).toLowerCase();
    if (base && (SUPPORTED_LOCALES as string[]).includes(base)) {
      return base as Locale;
    }
  }
  return 'en';
}
